// WorkspaceShell — the persistent operator tab shell (PRD-0085, slice 1).
//
// This is the element that wraps the operator work-object routes so the tab bar
// and tab state survive navigation between sibling routes. The
// `WorkspaceTabsProvider` is mounted ABOVE the routed `<Outlet/>` in `App.tsx`,
// so opening a feed item (which navigates to a sibling route) does NOT unmount
// the provider — the tab the operator just opened persists and the destination
// page renders underneath the same, still-visible tab bar.
//
// Routes under this shell (see App.tsx): `/home`, `/agent/:brandId`,
// `/brand/:brandId/review`, `/review/:runId`, `/runs/:runId`, `/gates/:gateId`,
// and brand artifact routes. Chat and Review are now treated as first-class
// workspace objects instead of separate full-screen exceptions.
//
// URL sync (both directions):
//   - When the URL matches an open tab's route, that tab becomes active
//     (covers deep-links and direct navigation).
//   - Clicking a tab / closing the active tab navigates to the resulting active
//     tab's route (or Home) — wired through the context's navigate callback.

import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { useBrand } from "../../hooks/useBrand";
import { useMinWidth } from "../../hooks/useMinWidth";
import { WorkspaceTabsProvider, useWorkspaceTabs } from "../../contexts/WorkspaceTabsContext";
import { parseBrandIdFromPath, resolveBrandContextId } from "../../lib/brand-navigation";
import { routeShellPanelEnabled, routeTabForUrl } from "../../lib/shell-routes";
import type { WorkTab } from "../../lib/workspace-tabs";
import { sectionForPath, sectionLabel, shellPanelAvailable } from "../../lib/workspace-sections";
import { ShellSidePanel } from "./ShellSidePanel";
import { WorkspaceTabBar } from "./WorkspaceTabBar";

const SHELL_OUTLET_CONTEXT = { shellOwnsChatPanel: true };

/** Derive a tab for an in-shell work-object path (deep-link/refresh) so the bar
 *  reflects the current surface. Returns null for non-shell paths. Delegates to
 *  the single shell-route registry (lib/shell-routes.ts). */
function shellTabForPath(pathname: string, search = ""): WorkTab | null {
  return routeTabForUrl(pathname, search);
}

/** Keeps the active tab in sync with the current URL (URL → tab state). */
function WorkspaceUrlSync() {
  const { tabs, activeId, syncActiveFromUrl, registerTabFromUrl } = useWorkspaceTabs();
  const { activeBrandId } = useActiveBrand();
  const location = useLocation();
  const prevRouteRef = useRef<string | null>(null);
  const prevBrandRef = useRef<string | null | undefined>(activeBrandId);
  // The brand the URL itself requests — pathname brandId for brand-scoped routes
  // (/agent/:brandId, /brand/:brandId/...) OR the `?brandId=` query for global
  // operator work routes (/review/:runId, /runs/:runId, ...). This is the second
  // half of the navigational key: a brand switch on a global operator route only
  // rewrites this query (pathname unchanged), so pathname alone can't tell us the
  // URL navigated. See lib/brand-navigation.ts (buildBrandScopedPath).
  //
  // Init asymmetry vs prevPathRef (intentional): prevPathRef starts `null` so the
  // initial URL counts as a "navigation" (pathChanged=true on first run, which
  // drives first-load deep-link registration). prevUrlBrandRef instead starts at
  // the already-resolved brand so urlBrandChanged=false on first run — harmless,
  // because urlNavigated is already true from pathChanged.
  const prevUrlBrandRef = useRef<string | null>(
    resolveBrandContextId(location.pathname, location.search),
  );

  useEffect(() => {
    const path = location.pathname;
    const route = `${path}${location.search}`;
    const routeChanged = prevRouteRef.current !== route;
    prevRouteRef.current = route;
    // The brand the URL is requesting right now (pathname brandId OR ?brandId= on
    // global operator routes). The navigational KEY is (pathname, urlBrandId): a
    // real brand switch ALWAYS changes one of them — the pathname for brand-scoped
    // routes, the query for global operator routes.
    const urlBrandId = resolveBrandContextId(path, location.search);
    const urlBrandChanged = prevUrlBrandRef.current !== urlBrandId;
    prevUrlBrandRef.current = urlBrandId;
    // "The URL navigated" = the navigational key moved. This unifies the
    // /agent/A → /agent/B pathname-switch case AND the /review?brandId=A →
    // ?brandId=B query-only-switch case: both must run registerTabFromUrl so the
    // work object registers into the NEW brand's slice and stays closeable,
    // instead of leaving the bar Home-only while the page renders the route.
    const urlNavigated = routeChanged || urlBrandChanged;
    // A brand switch swaps `tabs`/`activeId` to the NEW brand's slice. When the
    // ACTIVE brand changes but the URL did NOT navigate at all (neither pathname
    // nor requested brandId moved), the URL is a stale leftover from the previous
    // brand; we must NOT run the deep-link register branch (it would inject the old
    // URL's work object into the brand we just switched to and reset its tab set).
    // We only reconcile the active marker from the URL against the new brand's
    // existing tabs, restoring that brand's prior active tab.
    const brandChanged = prevBrandRef.current !== activeBrandId;
    // Cold-load / loading-order: on first paint `activeBrandId` is often null/
    // undefined and resolves to the real id asynchronously. That undefined→real
    // transition is NOT a brand SWITCH — it's RESOLUTION of the same (single)
    // brand we already deep-linked to. The URL's work object genuinely belongs to
    // the now-resolved brand, so it must still register; treating it as a switch
    // would skip registration and leave the bar Home-only while the page renders.
    const brandResolvedFromUnknown = prevBrandRef.current == null && activeBrandId != null;
    prevBrandRef.current = activeBrandId;

    // Find an OPEN tab whose route matches the current path. The pinned Home tab
    // matches `/home`, so direct navigation to `/home` lands on Home naturally.
    const match = tabs.find((t) => t.route === route);
    if (match) {
      if (match.id !== activeId) syncActiveFromUrl(match.id);
      return;
    }
    // The genuine "stale URL" suppression: the ACTIVE brand changed but the URL did
    // NOT navigate (neither pathname nor requested brandId) and it isn't the
    // cold-load undefined→real resolution → never register a tab; leave the new
    // brand's slice (and its restored active tab) untouched. Any URL navigation
    // (pathname OR query-only brandId switch) falls through to register the work
    // object into the new brand's slice so it stays closeable.
    if (brandChanged && !urlNavigated && !brandResolvedFromUnknown) return;
    // Deep-link / refresh / external link directly to an in-shell work object that
    // has no open tab → register one (without navigating; we are already here) so
    // URL, content, and the tab bar stay aligned and the work object is closeable.
    // Only when the URL actually navigated — not when this effect re-fires because
    // the tab list changed (e.g. closing a tab, where the URL is briefly stale and
    // we must NOT re-register the tab being closed). Non-shell paths leave the
    // active tab as-is so the user can return to their work tab. The brand-
    // resolution tick (undefined/null → real) is the one no-navigation case that
    // DOES register: the deep-linked object is committed to the now-known brand's slice.
    if (!urlNavigated && !brandResolvedFromUnknown) return;
    const deepLinked = shellTabForPath(path, location.search);
    if (deepLinked) registerTabFromUrl(deepLinked);
    // `tabs`/`activeId` are deps so a newly-opened tab gets active-synced on the
    // next run; the `urlNavigated` guard above keeps those re-runs from re-registering.
  }, [location.pathname, location.search, activeBrandId, tabs, activeId, syncActiveFromUrl, registerTabFromUrl]);

  return null;
}

function WorkspaceShellInner() {
  const location = useLocation();
  const { activeBrand } = useActiveBrand();
  const routeBrandId = parseBrandIdFromPath(location.pathname);
  const { data: routeBrandData } = useBrand(routeBrandId ?? undefined);
  const isDesktop = useMinWidth(768);
  const [desktopPanelCollapsed, setDesktopPanelCollapsed] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const section = sectionForPath(location.pathname);
  const docsPanelPinnedOpen = section === "docs";
  const shellContextLabel = sectionLabel(section);
  useEffect(() => {
    if (docsPanelPinnedOpen) {
      setDesktopPanelCollapsed(false);
      setMobilePanelOpen(true);
      return;
    }
    setMobilePanelOpen(false);
  }, [docsPanelPinnedOpen, section]);

  // The Home panel needs an active brand; don't surface the panel/toggle without one
  // (the welcome screen owns that state). Other shell-panel sections are brand-scoped by URL.
  const panelAvailable = routeShellPanelEnabled(location.pathname) && shellPanelAvailable(section, !!activeBrand);
  const canTogglePanel = panelAvailable && !docsPanelPinnedOpen;
  const showDesktopPanel = panelAvailable && (docsPanelPinnedOpen || !desktopPanelCollapsed);
  const shellBrandLabel = routeBrandData?.data?.name ?? activeBrand?.name ?? "Select a brand";

  return (
    <div className="flex min-h-screen flex-col pb-[var(--app-bottom-nav-safe-height)] md:pb-0">
      <WorkspaceUrlSync />
      <WorkspaceTabBar />
      {/* Subtoolbar: passive context crumb (brand › section). The brand switcher
          lives in the rail now — the rail is the single switcher source. */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 sm:px-6">
        <span className="max-w-[min(14rem,48vw)] truncate font-data text-[11px] font-medium text-slate-700 sm:max-w-xs md:max-w-sm">
          {shellBrandLabel}
        </span>
        <span aria-hidden className="text-slate-300">/</span>
        <span className="font-data text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
          {shellContextLabel}
        </span>
        {panelAvailable ? (
          <>
            {canTogglePanel ? (
              <>
                <button
                  type="button"
                  onClick={() => setMobilePanelOpen((open) => !open)}
                  aria-expanded={mobilePanelOpen}
                  className="ml-auto rounded-md px-2 py-1 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 md:hidden"
                >
                  {mobilePanelOpen ? "Hide context" : "Context"}
                </button>
                <button
                  type="button"
                  onClick={() => setDesktopPanelCollapsed((collapsed) => !collapsed)}
                  aria-pressed={desktopPanelCollapsed}
                  className="ml-auto hidden rounded-md px-2 py-1 font-data text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 md:inline-flex"
                >
                  {desktopPanelCollapsed ? "Show panel" : "Hide panel"}
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      {!isDesktop && panelAvailable && (docsPanelPinnedOpen || mobilePanelOpen) ? (
        <div
          data-testid="workspace-shell-mobile-panel"
          className="border-b border-slate-200 bg-slate-50 md:hidden"
        >
          <ShellSidePanel />
        </div>
      ) : null}
      <div className="min-h-0 flex-1 md:flex">
        {/* Shell-owned contextual side panel for migrated sections; null elsewhere.
            Unmigrated pages still render their own secondary content. Sticky
            so it stays in view while main content scrolls (matches the old page
            sidebars' sticky positioning behavior). */}
        {isDesktop && showDesktopPanel ? (
          <div className="hidden shrink-0 self-start md:block md:sticky md:top-[var(--workspace-tabbar-height)] md:h-[calc(100dvh-var(--workspace-tabbar-height)-2.75rem)]">
            <ShellSidePanel />
          </div>
        ) : null}
        <div className="min-h-0 min-w-0 flex-1 bg-white">
          <Outlet context={SHELL_OUTLET_CONTEXT} />
        </div>
      </div>
    </div>
  );
}

/**
 * The persistent shell element. Mount this ABOVE the operator routes (as the
 * element of a layout route whose children are the work-object routes) so the
 * provider + bar stay mounted across navigation.
 */
export function WorkspaceShell() {
  const navigate = useNavigate();
  return (
    <WorkspaceTabsProvider navigate={navigate}>
      <WorkspaceShellInner />
    </WorkspaceTabsProvider>
  );
}
